/**
 * The scoring engine (spec §12, D-17 – D-21).
 *
 *   setScore = (bodyweightKg × bwFactor + weightKg) × reps
 *
 *     duration exercises:  reps is seconds; substitute (seconds ÷ SECONDS_PER_REP)
 *     supported set:       setScore × SUPPORTED_MULTIPLIER
 *
 *   dailyScore = Σ setScore across every set that day
 *
 * This is the one implementation. Both clients import it, because two would diverge (D-09).
 * It imports nothing and reads no clock.
 */
import { exerciseById } from "./catalog.js";
/**
 * Duration work is converted to rep-equivalents at 3 seconds ≈ 1 rep (D-19).
 * A tuning constant, not a law — it lives here alone so it can be revised once real
 * data exists. Revising it does not fix stored scores; see C-11.
 */
export const SECONDS_PER_REP = 3;
/** A supported set scores half the whole set, bodyweight and load alike (D-18). */
export const SUPPORTED_MULTIPLIER = 0.5;
/**
 * The score for one set.
 *
 * `set.reps` is a rep count, or SECONDS when `exercise.measure` is "duration".
 * `bodyweightKg` is the workout's snapshot, never the user's current weight (D-20).
 *
 * Note the flag and an assisted catalog variant do not stack (D-21): `Assisted Pull-Up`
 * already carries a halved factor, so flagging it supported halves it twice. That is
 * documented rather than enforced — this function honours whatever it is given.
 */
export function scoreSet(set, exercise, bodyweightKg) {
    const effectiveReps = exercise.measure === "duration" ? set.reps / SECONDS_PER_REP : set.reps;
    const loadKg = bodyweightKg * exercise.bodyweightFactor + set.weightKg;
    const raw = loadKg * effectiveReps;
    return set.supported ? raw * SUPPORTED_MULTIPLIER : raw;
}
/**
 * The score for one logged exercise: the sum over its sets.
 *
 * The movement is resolved from `catalogId`. An id this build does not know scores 0
 * rather than throwing — a workout logged under a since-removed id must still open and
 * still total, and one unknown exercise must not take the whole day's score with it.
 */
export function scoreExercise(exercise, bodyweightKg) {
    const catalogEntry = exerciseById(exercise.catalogId);
    if (!catalogEntry)
        return 0;
    return exercise.sets.reduce((total, set) => total + scoreSet(set, catalogEntry, bodyweightKg), 0);
}
/**
 * The score for a whole day. Uses the workout's own snapshotted bodyweight, which is
 * what keeps past days frozen when today's weight changes (D-20).
 */
export function scoreWorkout(workout) {
    return workout.exercises.reduce((total, exercise) => total + scoreExercise(exercise, workout.bodyweightKg), 0);
}
//# sourceMappingURL=score.js.map